import styled from "@emotion/styled";
import { useField } from "formik";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  classNames,
  DEFAULT_INPUT_HEIGHT,
  ellipsise,
  Icon,
  IconButton,
  Labelish,
  SkiliticsIcon,
  Tooltip,
  useCommitChange,
  useFieldContext,
  useFieldLabelAndDescription,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { getNodeTitleByIdSelector } from "../../state";
import { useNodePickingActive, type NodePickingOptions } from "../picking";
import { NodePickingButton } from "./NodePickingButton.tsx";

/**
 * A field that allows the user to select a node by clicking on it in the canvas.
 * It displays the selected node's title and allows the user to remove the selection.
 * Stores the selected node's ID in the form state.
 */

export type FieldNodePickerProps = {
  criteria?: NodePickingOptions["criteria"];
  formatStringOverrides?: Partial<typeof formatStrings>;
};

export const FieldNodePicker = ({ criteria, formatStringOverrides }: FieldNodePickerProps) => {
  const commitChange = useCommitChange();
  const mergedFormatStrings = useMemo(() => ({ ...formatStrings, ...formatStringOverrides }), [formatStringOverrides]);
  const formatMessage = useFormatMessage(mergedFormatStrings);

  const nodePickingActive = useNodePickingActive();

  const { dataTest, name } = useFieldContext();
  const [label, description] = useFieldLabelAndDescription(name);

  const [{ value }, _, { setValue }] = useField<number | undefined>(name);

  const commitValue = (id?: number) => {
    setValue(id)
      .then(() => commitChange())
      .catch(console.warn);
  };

  const handleNodesPicked = ([id, ...__]: number[]) => commitValue(id);

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    commitValue(undefined);
  };

  const getNodeTitle = useSelector(getNodeTitleByIdSelector);

  const selectedNodeTitle = useMemo(() => {
    if (!value) return;
    return getNodeTitle(value);
  }, [getNodeTitle, value]);

  const options = useMemo(
    () => ({
      criteria,
      max: 1,
      onMaxReached: "deactivate" as const,
      toast: { message: formatMessage("toastMessage") },
      shield: { excludedAreas: ["layers-panel"] },
    }),
    [criteria, formatMessage],
  );

  return (
    <>
      <Tooltip title={description}>
        <Labelish>{label}</Labelish>
      </Tooltip>
      <NodePickingButton options={options} onNodesPicked={handleNodesPicked}>
        <Wrapper data-test={dataTest} className={classNames({ nodePickingActive })}>
          <SkiliticsIcon icon="select-element" size={16} />
          {!value && <PlaceholderLabel>{formatMessage("selectPlaceholder")}</PlaceholderLabel>}

          {value &&
            (selectedNodeTitle ? (
              <Label>{selectedNodeTitle}</Label>
            ) : (
              <NoNodeFoundLabel>
                <Icon icon="info-circle" />
                {formatMessage("invalid")}
              </NoNodeFoundLabel>
            ))}
          <IconButton icon="minus" onClick={handleRemoveClick} data-test="remove-picked-node-button" />
        </Wrapper>
      </NodePickingButton>
    </>
  );
};

const Label = styled.div`
  border-bottom: ${({ theme }) => `1px solid ${theme.surfaceSoft}`};
  padding: 8px 0;
  height: ${DEFAULT_INPUT_HEIGHT}px;
  width: 100%;
  transition: all 0.2s ease-in-out;
  min-width: 0; // css hack to allow flex child to ellipsise.
  user-select: none;
  ${ellipsise};
`;

const NoNodeFoundLabel = styled(Label)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  color: ${({ theme }) => theme.onWarningContainer};
  background: ${({ theme }) => theme.warningContainer};
  border-color: ${({ theme }) => theme.warning};
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
  cursor: pointer;
  overflow: hidden;
  transition: color 0.2s ease-in-out;

  &:hover,
  &:active,
  &.nodePickingActive {
    color: ${({ theme }) => theme.primary};
    ${Label} {
      background: ${({ theme }) => theme.surface};
      border-color: ${({ theme }) => theme.outline};
    }
  }
`;

const PlaceholderLabel = styled(Label)`
  opacity: 0.5;
`;

const formatStrings = {
  toastMessage: {
    en: "Select an *element* on the canvas or layers panel",
  },
  selectPlaceholder: {
    en: "Select an element",
  },
  cancel: {
    en: "Cancel",
  },
  invalid: {
    en: "Invalid selection",
  },
} satisfies FormatMessages;
